import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../services';
import {
  Button,
  Card,
  Modal,
  Input,
  Select,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
} from '../UI';
import { 
  Calendar, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Users,
  FileText,
  Filter,
  Download,
  Edit3,
  Eye,
  RefreshCw
} from 'lucide-react';

// Leave accrues in whole or half days — render 1.5 as "1.5" and 2 as "2".
const formatDays = (n) => {
  const value = Number(n) || 0;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

// parseInt would silently drop 11.5 to 11.
const parseHalfDays = (value) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 2) / 2;
};

const EmployeeLeaveManagement = () => {
  const [activeSubTab, setActiveSubTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [allLeaveRequests, setAllLeaveRequests] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveStats, setLeaveStats] = useState(null);
  const [clientLeaveSettings, setClientLeaveSettings] = useState(null);

  // Bulk selection states
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState(''); // 'approve' or 'reject'
  const [bulkComments, setBulkComments] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Modal states
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [reviewComments, setReviewComments] = useState('');
  const [balanceForm, setBalanceForm] = useState({
    sick: 12,
    casual: 12,
    earned: 15,
    compensatory: 0
  });
  const [editForm, setEditForm] = useState({
    leaveType: 'casual',
    startDate: '',
    endDate: '',
    reason: '',
    isHalfDay: false,
    halfDayType: 'first_half',
  });

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    leaveType: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  useEffect(() => {
    fetchData();
  }, [activeSubTab, filters]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeSubTab === 'pending') {
        const response = await leaveAPI.getPendingLeaves();
        setPendingLeaves(response.data || []);
      } else if (activeSubTab === 'all') {
        const response = await leaveAPI.getLeaveRequests({
          status: filters.status || undefined,
          leaveType: filters.leaveType || undefined,
          all: true
        });
        setAllLeaveRequests(response.data || []);
      } else if (activeSubTab === 'balances') {
        const response = await leaveAPI.getAllLeaveBalances({ year: filters.year });
        setLeaveBalances(response.data || []);
        if (response.leaveSettings) {
          setClientLeaveSettings(response.leaveSettings);
        }
      } else if (activeSubTab === 'stats') {
        const response = await leaveAPI.getLeaveStats({ year: filters.year });
        setLeaveStats(response.data || null);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      await leaveAPI.approveLeave(selectedRequest._id, reviewComments);
      setSuccess('Leave request approved successfully');
      setShowReviewModal(false);
      setSelectedRequest(null);
      setReviewComments('');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to approve leave');
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !reviewComments.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    try {
      await leaveAPI.rejectLeave(selectedRequest._id, reviewComments);
      setSuccess('Leave request rejected');
      setShowReviewModal(false);
      setSelectedRequest(null);
      setReviewComments('');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reject leave');
    }
  };

  const handleUpdateBalance = async () => {
    if (!selectedEmployee) return;
    try {
      await leaveAPI.updateLeaveBalance(selectedEmployee._id, {
        year: filters.year,
        ...balanceForm
      });
      setSuccess('Leave balance updated successfully');
      setShowBalanceModal(false);
      setSelectedEmployee(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update balance');
    }
  };

  const handleSyncBalances = async () => {
    const confirmMsg = 'This rebuilds every employee\'s monthly credits for '
      + filters.year
      + ' from the current organization leave settings. Days already used stay as they are, '
      + 'and manual adjustments are kept. Continue?';

    if (!window.confirm(confirmMsg)) return;

    try {
      setLoading(true);
      const response = await leaveAPI.syncLeaveBalances({ year: filters.year });
      setSuccess(response.message || 'Leave balances synced successfully');
      fetchData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Failed to sync balances');
      setLoading(false);
    }
  };

  const openReviewModal = (request) => {
    setSelectedRequest(request);
    setReviewComments('');
    setShowReviewModal(true);
  };

  const openBalanceModal = (employeeData) => {
    setSelectedEmployee(employeeData.employee);
    setBalanceForm({
      sick: employeeData.balance.sick.total,
      casual: employeeData.balance.casual.total,
      earned: employeeData.balance.earned.total,
      compensatory: employeeData.balance.compensatory.total
    });
    setShowBalanceModal(true);
  };

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setReviewComments('');
    setShowDetailModal(true);
  };

  const openEditModal = (request) => {
    setSelectedRequest(request);
    setEditForm({
      leaveType: request.leaveType,
      startDate: new Date(request.startDate).toISOString().split('T')[0],
      endDate: new Date(request.endDate).toISOString().split('T')[0],
      reason: request.reason || '',
      isHalfDay: request.isHalfDay || false,
      halfDayType: request.halfDayType || 'first_half',
    });
    setShowEditModal(true);
  };

  const handleAdminUpdateLeave = async (e) => {
    e.preventDefault();
    if (!selectedRequest) return;
    if (!editForm.startDate || (!editForm.isHalfDay && !editForm.endDate)) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      await leaveAPI.adminUpdateLeave(selectedRequest._id, editForm);
      setSuccess('Leave request updated successfully');
      setShowEditModal(false);
      setSelectedRequest(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update leave request');
    }
  };

  // Bulk approval handlers
  const toggleSelectLeave = (leaveId) => {
    setSelectedLeaves(prev => 
      prev.includes(leaveId) 
        ? prev.filter(id => id !== leaveId)
        : [...prev, leaveId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeaves.length === pendingLeaves.length) {
      setSelectedLeaves([]);
    } else {
      setSelectedLeaves(pendingLeaves.map(leave => leave._id));
    }
  };

  const openBulkModal = (action) => {
    if (selectedLeaves.length === 0) {
      setError('Please select at least one leave request');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setBulkAction(action);
    setBulkComments('');
    setShowBulkModal(true);
  };

  const handleBulkAction = async () => {
    if (bulkAction === 'reject' && !bulkComments.trim()) {
      setError('Please provide a reason for bulk rejection');
      return;
    }

    setBulkProcessing(true);
    try {
      let response;
      if (bulkAction === 'approve') {
        response = await leaveAPI.bulkApproveLeaves(selectedLeaves, bulkComments);
      } else {
        response = await leaveAPI.bulkRejectLeaves(selectedLeaves, bulkComments);
      }

      const successCount = response.data?.success?.length || 0;
      const failedCount = response.data?.failed?.length || 0;

      setSuccess(`Bulk ${bulkAction}: ${successCount} successful, ${failedCount} failed`);
      setShowBulkModal(false);
      setSelectedLeaves([]);
      setBulkComments('');
      fetchData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || `Failed to ${bulkAction} leaves`);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleAdminCancel = async () => {
    if (!selectedRequest) return;
    if (!window.confirm('Are you sure you want to cancel this approved leave? The employee\'s leave balance will be restored.')) return;
    try {
      await leaveAPI.adminCancelLeave(selectedRequest._id, reviewComments);
      setSuccess('Approved leave cancelled successfully');
      setShowDetailModal(false);
      setSelectedRequest(null);
      setReviewComments('');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to cancel approved leave');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getLeaveTypeBadge = (type) => {
    const styles = {
      sick: 'bg-red-50 text-red-700',
      casual: 'bg-blue-50 text-blue-700',
      earned: 'bg-green-50 text-green-700',
      unpaid: 'bg-gray-50 text-gray-700',
      compensatory: 'bg-purple-50 text-purple-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${styles[type]}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4">
        {[
          { id: 'pending', label: 'Pending Requests', icon: Clock },
          { id: 'all', label: 'All Requests', icon: FileText },
          { id: 'balances', label: 'Leave Balances', icon: Users },
          { id: 'stats', label: 'Statistics', icon: Calendar }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeSubTab === tab.id
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Messages */}
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {/* Pending Requests Tab */}
          {activeSubTab === 'pending' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  Pending Leave Requests ({pendingLeaves.length})
                </h3>
                {pendingLeaves.length > 0 && (
                  <div className="flex items-center gap-2">
                    {selectedLeaves.length > 0 && (
                      <>
                        <span className="text-sm text-gray-600">
                          {selectedLeaves.length} selected
                        </span>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => openBulkModal('approve')}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Bulk Approve
                        </Button>
                        <Button
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white"
                          onClick={() => openBulkModal('reject')}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Bulk Reject
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {pendingLeaves.length === 0 ? (
                <Card>
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-400" />
                    <p className="text-lg font-medium">All caught up!</p>
                    <p className="text-sm">No pending leave requests to review.</p>
                  </div>
                </Card>
              ) : (
                <>
                  {pendingLeaves.length > 1 && (
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={selectedLeaves.length === pendingLeaves.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 font-medium">
                        Select All ({pendingLeaves.length})
                      </span>
                    </div>
                  )}
                  <div className="grid gap-4">
                    {pendingLeaves.map((request) => (
                      <Card key={request._id} className="hover:shadow-md transition-shadow">
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedLeaves.includes(request._id)}
                              onChange={() => toggleSelectLeave(request._id)}
                              className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                      <span className="text-indigo-600 font-semibold">
                                        {request.employee?.name?.charAt(0) || '?'}
                                      </span>
                                    </div>
                                    <div>
                                      <p className="font-semibold text-gray-900">{request.employee?.name}</p>
                                      <p className="text-sm text-gray-500">{request.employee?.department || 'N/A'}</p>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {getLeaveTypeBadge(request.leaveType)}
                                    <span className="text-sm text-gray-600">
                                      {formatDate(request.startDate)} - {formatDate(request.endDate)}
                                    </span>
                                    <span className="text-sm font-medium text-gray-700">
                                      ({request.numberOfDays} {request.numberOfDays === 1 ? 'day' : 'days'})
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 line-clamp-2">{request.reason}</p>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openDetailModal(request)}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditModal(request)}
                                    title="Edit Leave"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => openReviewModal(request)}
                                  >
                                    Review
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* All Requests Tab */}
          {activeSubTab === 'all' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Filters:</span>
                </div>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-40"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
                <Select
                  value={filters.leaveType}
                  onChange={(e) => setFilters({ ...filters, leaveType: e.target.value })}
                  className="w-40"
                >
                  <option value="">All Types</option>
                  <option value="sick">Sick</option>
                  <option value="casual">Casual</option>
                  <option value="earned">Earned</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="compensatory">Compensatory</option>
                </Select>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allLeaveRequests.map((request) => (
                      <tr key={request._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                              <span className="text-indigo-600 text-sm font-medium">
                                {request.employee?.name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{request.employee?.name}</p>
                              <p className="text-xs text-gray-500">{request.employee?.department}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">{getLeaveTypeBadge(request.leaveType)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(request.startDate)} - {formatDate(request.endDate)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{request.numberOfDays}</td>
                        <td className="px-4 py-3">{getStatusBadge(request.status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => openDetailModal(request)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {['pending', 'approved'].includes(request.status) && (
                              <Button size="sm" variant="outline" onClick={() => openEditModal(request)} title="Edit Leave">
                                <Edit3 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Leave Balances Tab */}
          {activeSubTab === 'balances' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Employee Leave Balances - {filters.year}
                </h3>
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSyncBalances}
                    title="Rebuild monthly leave credits from organization leave settings"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Sync from Settings
                  </Button>
                  <Select
                    value={filters.year}
                    onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
                    className="w-32"
                  >
                    {[2024, 2025, 2026, 2027].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Leave Policy Info */}
              {clientLeaveSettings?.leaveTypes && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium mb-1">Active Leave Types (from Organization Settings)</p>
                  <div className="flex flex-wrap gap-2">
                    {clientLeaveSettings.leaveTypes.sick?.enabled !== false && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Sick: {clientLeaveSettings.leaveTypes.sick?.annualEntitlement || 0} days/year</span>
                    )}
                    {clientLeaveSettings.leaveTypes.casual?.enabled !== false && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Casual: {clientLeaveSettings.leaveTypes.casual?.annualEntitlement || 0} days/year</span>
                    )}
                    {clientLeaveSettings.leaveTypes.earned?.enabled !== false && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Earned: {clientLeaveSettings.leaveTypes.earned?.annualEntitlement || 0} days/year</span>
                    )}
                    {clientLeaveSettings.leaveTypes.compensatory?.enabled !== false && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Comp Off</span>
                    )}
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">Unpaid: Always enabled</span>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.sick?.enabled !== false) && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Sick</th>
                      )}
                      {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.casual?.enabled !== false) && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Casual</th>
                      )}
                      {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.earned?.enabled !== false) && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Earned</th>
                      )}
                      {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.compensatory?.enabled !== false) && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Comp Off</th>
                      )}
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Unpaid Used</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {leaveBalances.map((item) => (
                      <tr key={item.employee._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                              <span className="text-indigo-600 text-sm font-medium">
                                {item.employee.name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.employee.name}</p>
                              <p className="text-xs text-gray-500">{item.employee.department}</p>
                            </div>
                          </div>
                        </td>
                        {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.sick?.enabled !== false) && (
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm">
                            <span className="font-medium text-gray-900">{formatDays(item.balance.sick.total - item.balance.sick.used - item.balance.sick.pending)}</span>
                            <span className="text-gray-400">/{formatDays(item.balance.sick.total)}</span>
                          </span>
                          {item.accrual?.sick?.effectiveEntitlement > 0 && (
                            <span className="block text-[11px] text-gray-400">
                              of {formatDays(item.accrual.sick.effectiveEntitlement)} this year
                            </span>
                          )}
                        </td>
                        )}
                        {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.casual?.enabled !== false) && (
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm">
                            <span className="font-medium text-gray-900">{formatDays(item.balance.casual.total - item.balance.casual.used - item.balance.casual.pending)}</span>
                            <span className="text-gray-400">/{formatDays(item.balance.casual.total)}</span>
                          </span>
                          {item.accrual?.casual?.effectiveEntitlement > 0 && (
                            <span className="block text-[11px] text-gray-400">
                              of {formatDays(item.accrual.casual.effectiveEntitlement)} this year
                            </span>
                          )}
                        </td>
                        )}
                        {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.earned?.enabled !== false) && (
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm">
                            <span className="font-medium text-gray-900">{formatDays(item.balance.earned.total - item.balance.earned.used - item.balance.earned.pending)}</span>
                            <span className="text-gray-400">/{formatDays(item.balance.earned.total)}</span>
                          </span>
                          {item.accrual?.earned?.effectiveEntitlement > 0 && (
                            <span className="block text-[11px] text-gray-400">
                              of {formatDays(item.accrual.earned.effectiveEntitlement)} this year
                            </span>
                          )}
                        </td>
                        )}
                        {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.compensatory?.enabled !== false) && (
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm">
                            <span className="font-medium text-gray-900">{formatDays(item.balance.compensatory.total - item.balance.compensatory.used - item.balance.compensatory.pending)}</span>
                            <span className="text-gray-400">/{formatDays(item.balance.compensatory.total)}</span>
                          </span>
                          {item.accrual?.compensatory?.effectiveEntitlement > 0 && (
                            <span className="block text-[11px] text-gray-400">
                              of {formatDays(item.accrual.compensatory.effectiveEntitlement)} this year
                            </span>
                          )}
                        </td>
                        )}
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium text-gray-900">{item.balance.unpaid.used}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button size="sm" variant="outline" onClick={() => openBalanceModal(item)}>
                            <Edit3 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Statistics Tab */}
          {activeSubTab === 'stats' && leaveStats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
                  <div className="p-4 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
                    <p className="text-2xl font-bold text-yellow-700">{leaveStats.pendingCount || 0}</p>
                    <p className="text-sm text-yellow-600">Pending Requests</p>
                  </div>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <div className="p-4 text-center">
                    <Calendar className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                    <p className="text-2xl font-bold text-blue-700">{leaveStats.onLeaveToday || 0}</p>
                    <p className="text-sm text-blue-600">On Leave Today</p>
                  </div>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <div className="p-4 text-center">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    <p className="text-2xl font-bold text-green-700">
                      {leaveStats.typeStats?.reduce((acc, t) => acc + t.totalDays, 0) || 0}
                    </p>
                    <p className="text-sm text-green-600">Total Days Used</p>
                  </div>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <div className="p-4 text-center">
                    <Users className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                    <p className="text-2xl font-bold text-purple-700">
                      {leaveStats.typeStats?.reduce((acc, t) => acc + t.count, 0) || 0}
                    </p>
                    <p className="text-sm text-purple-600">Total Requests</p>
                  </div>
                </Card>
              </div>

              {/* Leave Type Distribution */}
              {leaveStats.typeStats && leaveStats.typeStats.length > 0 && (
                <Card title="Leave Type Distribution">
                  <div className="p-4">
                    <div className="space-y-4">
                      {leaveStats.typeStats.map((stat) => (
                        <div key={stat._id} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {getLeaveTypeBadge(stat._id)}
                            <span className="text-sm text-gray-600">{stat.count} requests</span>
                          </div>
                          <span className="text-lg font-semibold text-gray-900">{stat.totalDays} days</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* On Leave Today */}
              {leaveStats.onLeaveTodayList && leaveStats.onLeaveTodayList.length > 0 && (
                <Card title="On Leave Today">
                  <div className="p-4">
                    <div className="grid gap-3">
                      {leaveStats.onLeaveTodayList.map((leave) => (
                        <div key={leave._id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <span className="text-indigo-600 font-medium">
                              {leave.employee?.name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{leave.employee?.name}</p>
                            <p className="text-sm text-gray-500">{leave.employee?.department}</p>
                          </div>
                          {getLeaveTypeBadge(leave.leaveType)}
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}
        </>
      )}

      {/* Review Modal */}
      <Modal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        title="Review Leave Request"
      >
        {selectedRequest && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Employee</span>
                <span className="font-medium">{selectedRequest.employee?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Leave Type</span>
                {getLeaveTypeBadge(selectedRequest.leaveType)}
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Duration</span>
                <span className="text-sm">{formatDate(selectedRequest.startDate)} - {formatDate(selectedRequest.endDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Days</span>
                <span className="font-medium">{selectedRequest.numberOfDays}</span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Reason</span>
                <p className="mt-1 text-sm text-gray-900">{selectedRequest.reason}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comments (required for rejection)
              </label>
              <textarea
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                placeholder="Add your comments..."
              />
            </div>

            <div className="flex gap-3">
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleApprove}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Approve
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleReject}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Balance Edit Modal */}
      <Modal
        isOpen={showBalanceModal}
        onClose={() => setShowBalanceModal(false)}
        title="Adjust Leave Balance"
      >
        {selectedEmployee && (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-indigo-600 font-semibold text-lg">
                  {selectedEmployee.name?.charAt(0) || '?'}
                </span>
              </div>
              <p className="font-medium text-gray-900">{selectedEmployee.name}</p>
              <p className="text-sm text-gray-500">{selectedEmployee.department}</p>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
              These are the days credited so far, not the yearly entitlement. Changing a figure
              records a dated adjustment in the employee's credit history — it does not stop the
              normal monthly accrual from continuing.
            </div>

            <div className="grid grid-cols-2 gap-4">
              {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.sick?.enabled !== false) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sick Leave</label>
                <Input
                  type="number"
                  value={balanceForm.sick}
                  onChange={(e) => setBalanceForm({ ...balanceForm, sick: parseHalfDays(e.target.value) })}
                  min={0}
                  step={0.5}
                />
              </div>
              )}
              {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.casual?.enabled !== false) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Casual Leave</label>
                <Input
                  type="number"
                  value={balanceForm.casual}
                  onChange={(e) => setBalanceForm({ ...balanceForm, casual: parseHalfDays(e.target.value) })}
                  min={0}
                  step={0.5}
                />
              </div>
              )}
              {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.earned?.enabled !== false) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Earned Leave</label>
                <Input
                  type="number"
                  value={balanceForm.earned}
                  onChange={(e) => setBalanceForm({ ...balanceForm, earned: parseHalfDays(e.target.value) })}
                  min={0}
                  step={0.5}
                />
              </div>
              )}
              {(!clientLeaveSettings || clientLeaveSettings.leaveTypes?.compensatory?.enabled !== false) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Compensatory</label>
                <Input
                  type="number"
                  value={balanceForm.compensatory}
                  onChange={(e) => setBalanceForm({ ...balanceForm, compensatory: parseHalfDays(e.target.value) })}
                  min={0}
                  step={0.5}
                />
              </div>
              )}
            </div>

            <Button className="w-full" onClick={handleUpdateBalance}>
              Save Adjustment
            </Button>
          </div>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Leave Request Details"
      >
        {selectedRequest && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Status</span>
                {getStatusBadge(selectedRequest.status)}
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Employee</span>
                <span className="font-medium">{selectedRequest.employee?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Department</span>
                <span className="text-sm">{selectedRequest.employee?.department || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Leave Type</span>
                {getLeaveTypeBadge(selectedRequest.leaveType)}
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Start Date</span>
                <span className="text-sm">{formatDate(selectedRequest.startDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">End Date</span>
                <span className="text-sm">{formatDate(selectedRequest.endDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Number of Days</span>
                <span className="font-medium">{selectedRequest.numberOfDays}</span>
              </div>
              {selectedRequest.isHalfDay && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Half Day</span>
                  <span className="text-sm capitalize">{selectedRequest.halfDayType}</span>
                </div>
              )}
              <div className="border-t pt-3">
                <span className="text-sm text-gray-500">Reason</span>
                <p className="mt-1 text-sm text-gray-900">{selectedRequest.reason}</p>
              </div>
              {selectedRequest.emergencyContact && (
                <div>
                  <span className="text-sm text-gray-500">Emergency Contact</span>
                  <p className="mt-1 text-sm text-gray-900">{selectedRequest.emergencyContact}</p>
                </div>
              )}
              {selectedRequest.reviewComments && (
                <div className="border-t pt-3">
                  <span className="text-sm text-gray-500">Review Comments</span>
                  <p className="mt-1 text-sm text-gray-900">{selectedRequest.reviewComments}</p>
                </div>
              )}
              {selectedRequest.reviewedBy && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Reviewed By</span>
                  <span className="text-sm">{selectedRequest.reviewedBy?.name}</span>
                </div>
              )}
              {selectedRequest.reviewedAt && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Reviewed At</span>
                  <span className="text-sm">{formatDate(selectedRequest.reviewedAt)}</span>
                </div>
              )}
            </div>

            {/* Admin Cancel button — only for approved leaves whose start date is in the future */}
            {selectedRequest.status === 'approved' && new Date(selectedRequest.startDate) > new Date() && (
              <div className="border-t pt-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cancel Reason (optional)</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows={2}
                    placeholder="Reason for cancelling this approved leave..."
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                  />
                </div>
                <Button 
                  className="w-full bg-red-600 hover:bg-red-700 text-white" 
                  onClick={handleAdminCancel}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel Approved Leave
                </Button>
              </div>
            )}

            {/* Edit Leave button — for pending and approved leaves */}
            {['pending', 'approved'].includes(selectedRequest.status) && (
              <Button
                variant="outline"
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => { setShowDetailModal(false); openEditModal(selectedRequest); }}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Leave Details
              </Button>
            )}

            <Button variant="outline" className="w-full" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </div>
        )}
      </Modal>

      {/* Bulk Action Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title={`Bulk ${bulkAction === 'approve' ? 'Approve' : 'Reject'} Leaves`}
        size="md"
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              You are about to {bulkAction} <span className="font-semibold">{selectedLeaves.length}</span> leave request(s).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comments {bulkAction === 'reject' && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={bulkComments}
              onChange={(e) => setBulkComments(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={bulkAction === 'approve' ? 'Optional comments...' : 'Please provide a reason for rejection...'}
              required={bulkAction === 'reject'}
            />
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowBulkModal(false)}
              disabled={bulkProcessing}
            >
              Cancel
            </Button>
            <Button
              className={`flex-1 ${bulkAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
              onClick={handleBulkAction}
              disabled={bulkProcessing || (bulkAction === 'reject' && !bulkComments.trim())}
            >
              {bulkProcessing ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  {bulkAction === 'approve' ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Confirm {bulkAction === 'approve' ? 'Approval' : 'Rejection'}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
      {/* Edit Leave Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Leave Request"
      >
        {selectedRequest && (
          <form onSubmit={handleAdminUpdateLeave} className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Editing leave for <span className="font-semibold">{selectedRequest.employee?.name}</span>. Leave balance will be adjusted automatically.
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
              <select
                value={editForm.leaveType}
                onChange={(e) => setEditForm({ ...editForm, leaveType: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="casual">Casual Leave</option>
                <option value="sick">Sick Leave</option>
                <option value="earned">Earned Leave</option>
                <option value="unpaid">Unpaid Leave</option>
                <option value="compensatory">Compensatory Off</option>
              </select>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.isHalfDay}
                  onChange={(e) => setEditForm({ ...editForm, isHalfDay: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">Half Day</span>
              </label>
              {editForm.isHalfDay && (
                <select
                  value={editForm.halfDayType}
                  onChange={(e) => setEditForm({ ...editForm, halfDayType: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
                >
                  <option value="first_half">First Half</option>
                  <option value="second_half">Second Half</option>
                </select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={editForm.endDate}
                  onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                  min={editForm.startDate}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required={!editForm.isHalfDay}
                  disabled={editForm.isHalfDay}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <textarea
                value={editForm.reason}
                onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Reason for leave..."
                required
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                <Edit3 className="w-4 h-4 mr-2" />
                Update Leave
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default EmployeeLeaveManagement;
